/**
 * In-world throne for the Last Touch game. Renders inside the main lobby's R3F
 * canvas at THRONE_POSITION. When `currentKing !== PublicKey.default`, an exact
 * copy of the king player's avatar (the same skin-colored capsule + name/face,
 * via <PlayerAvatarBody>) is locked at the throne, crowned, with a "👑 <name>"
 * label — or "👑 you" when the local wallet is king.
 *
 * The king's appearance is resolved from the connected player whose `pubkey`
 * matches `currentKing` (passed down from the lobby). When that king isn't in
 * the room — they touched then left, or this is the /watch spectator view — we
 * fall back to the deterministic color their pubkey would derive, so the copy
 * still looks like a player avatar.
 *
 * The king's first-person camera is NOT moved — per design, the player who is
 * king keeps walking around freely; only OTHER players see the king-on-throne
 * copy.
 *
 * Game-state text (pot, king name, time left) lives on wall-mounted plaques
 * elsewhere in the hall (KingNameplate, TokenSign, RoundTimerPlaque) — not
 * floating above the throne. Throne meshes here stay purely physical.
 */
import { Billboard, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { RigidBody } from "@react-three/rapier";
import { PublicKey } from "@solana/web3.js";
import { useRef } from "react";
import * as THREE from "three";

import { PlayerAvatarBody } from "../../scene/EcctrlAvatar";
import type { KothRoundState } from "./useKothRound";

export const THRONE_POSITION: [number, number, number] = [0, 0, 0];

interface KothThroneStationProps {
  round: KothRoundState | null;
  localWalletPubkey: PublicKey | null;
  /** Appearance of the connected player who is the current king (matched by
   *  pubkey in the lobby). Absent when the king isn't in the room. */
  kingColorHex?: string;
  kingDisplayName?: string;
  /** Colyseus sessionId of the king, for the talking-face billboard. */
  kingIdentity?: string;
}

export function KothThroneStation({
  round,
  localWalletPubkey,
  kingColorHex,
  kingDisplayName,
  kingIdentity,
}: KothThroneStationProps): JSX.Element {
  const hasKing =
    !!round && !round.currentKing.equals(PublicKey.default) && round.status === 0;
  const isYouKing =
    hasKing && !!localWalletPubkey && round!.currentKing.equals(localWalletPubkey);
  const kingPubkey = hasKing ? round!.currentKing.toBase58() : "";
  const kingShort = hasKing ? shortPk(round!.currentKing) : "";

  return (
    <group position={THRONE_POSITION}>
      <ThroneMesh active={hasKing} />
      {hasKing && (
        <KingAvatar
          isYouKing={isYouKing}
          kingShort={kingShort}
          kingPubkey={kingPubkey}
          colorHex={kingColorHex}
          displayName={kingDisplayName}
          identity={kingIdentity}
        />
      )}
    </group>
  );
}

interface ThroneMeshProps {
  active: boolean;
}
function ThroneMesh({ active }: ThroneMeshProps): JSX.Element {
  const crownRef = useRef<THREE.Mesh>(null);
  useFrame((_, dt) => {
    if (crownRef.current) crownRef.current.rotation.y += dt * 0.5;
  });
  const glowColor = active ? "#ffd070" : "#5a4a18";
  const glowIntensity = active ? 2.2 : 0.4;
  return (
    <group>
      {/* Tall imposing throne — built from primitives to match the reference
          composition (the dais itself is owned by ThroneHall.tsx). The throne
          sits on top of the third dais step at y=0.8. Facing +z (entrance).

          Layers from bottom to top:
            - wide gold-trimmed wood base
            - red cushion seat
            - tall arched back with gold filigree
            - two short armrests
            - peaked top with floating crown
      */}
      <ThroneBody active={active} />
      {/* floating crown above the throne's peaked top */}
      <mesh ref={crownRef} position={[0, 5.6, -0.65]} castShadow>
        <coneGeometry args={[0.45, 0.7, 8]} />
        <meshStandardMaterial
          color="#f4d36a"
          emissive={glowColor}
          emissiveIntensity={glowIntensity * 0.4}
          metalness={0.7}
          roughness={0.25}
        />
      </mesh>
      <pointLight
        position={[0, 5.8, 0]}
        intensity={glowIntensity}
        color={glowColor}
        distance={16}
      />
    </group>
  );
}

interface ThroneBodyProps {
  active: boolean;
}
function ThroneBody({ active }: ThroneBodyProps): JSX.Element {
  const goldEmissive = active ? 0.6 : 0.25;
  return (
    // Each child <mesh> generates a cuboid collider — base, back panel, and
    // armrests are what actually block the avatar; the thin trim/inlay/finial
    // colliders are harmless overlap inside those.
    <RigidBody type="fixed" colliders="cuboid" position={[0, 0.8, 0]}>
      {/* wide wooden base block */}
      <mesh position={[0, 0.3, 0]} castShadow receiveShadow>
        <boxGeometry args={[2.0, 0.6, 1.6]} />
        <meshStandardMaterial color="#3a2818" roughness={0.7} />
      </mesh>
      {/* gold trim around the base */}
      <mesh position={[0, 0.65, 0]} castShadow>
        <boxGeometry args={[2.1, 0.08, 1.7]} />
        <meshStandardMaterial
          color="#c8a14a"
          emissive="#6a4a08"
          emissiveIntensity={goldEmissive * 0.4}
          metalness={0.6}
          roughness={0.35}
        />
      </mesh>
      {/* red cushion seat */}
      <mesh position={[0, 0.78, 0.05]} castShadow>
        <boxGeometry args={[1.7, 0.22, 1.3]} />
        <meshStandardMaterial color="#7a1818" roughness={0.7} />
      </mesh>
      {/* tall back panel — dark wood */}
      <mesh position={[0, 2.3, -0.65]} castShadow receiveShadow>
        <boxGeometry args={[1.8, 3.0, 0.18]} />
        <meshStandardMaterial color="#2a1a08" roughness={0.6} />
      </mesh>
      {/* gold inlay on the back panel — vertical strip */}
      <mesh position={[0, 2.3, -0.55]} castShadow>
        <boxGeometry args={[0.4, 2.6, 0.04]} />
        <meshStandardMaterial
          color="#caa14a"
          emissive="#7a4a08"
          emissiveIntensity={goldEmissive * 0.5}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>
      {/* peaked top — pyramid finial */}
      <mesh position={[0, 4.0, -0.65]} castShadow rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[0.55, 0.8, 4]} />
        <meshStandardMaterial
          color="#caa14a"
          emissive="#6a4a08"
          emissiveIntensity={goldEmissive * 0.6}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>
      {/* left armrest */}
      <mesh position={[-1.0, 1.05, 0.1]} castShadow>
        <boxGeometry args={[0.25, 0.32, 1.2]} />
        <meshStandardMaterial color="#3a2818" roughness={0.65} />
      </mesh>
      {/* right armrest */}
      <mesh position={[1.0, 1.05, 0.1]} castShadow>
        <boxGeometry args={[0.25, 0.32, 1.2]} />
        <meshStandardMaterial color="#3a2818" roughness={0.65} />
      </mesh>
      {/* gold caps on armrests */}
      <mesh position={[-1.0, 1.25, 0.7]} castShadow>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshStandardMaterial
          color="#caa14a"
          emissive="#7a4a08"
          emissiveIntensity={goldEmissive * 0.5}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>
      <mesh position={[1.0, 1.25, 0.7]} castShadow>
        <sphereGeometry args={[0.16, 16, 16]} />
        <meshStandardMaterial
          color="#caa14a"
          emissive="#7a4a08"
          emissiveIntensity={goldEmissive * 0.5}
          metalness={0.7}
          roughness={0.3}
        />
      </mesh>
    </RigidBody>
  );
}

interface KingAvatarProps {
  isYouKing: boolean;
  kingShort: string;
  kingPubkey: string;
  colorHex?: string;
  displayName?: string;
  identity?: string;
}

/**
 * The king-on-throne: an exact copy of the player's avatar (same
 * <PlayerAvatarBody> capsule + skin color + talking face) wearing a crown. The
 * capsule sits at the group origin, so the group is raised to seat it on the
 * throne and the crown rides just above the capsule's head.
 */
function KingAvatar({
  isYouKing,
  kingShort,
  kingPubkey,
  colorHex,
  displayName,
  identity,
}: KingAvatarProps): JSX.Element {
  return (
    <group position={[0, 2.0, 0]}>
      {/* Exact player avatar — its own name label is suppressed in favor of the
          crowned 👑 label below; colorIdentity falls back to the king's pubkey
          so a disconnected king still gets their deterministic color. */}
      <PlayerAvatarBody
        colorHex={colorHex}
        colorIdentity={kingPubkey}
        identity={identity}
        showName={false}
      />
      <Crown />
      <Billboard position={[0, 1.25, 0]}>
        <Text
          fontSize={0.22}
          color={isYouKing ? "#ffd66b" : "#e8e8ef"}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.015}
          outlineColor="#0a0a10"
        >
          {isYouKing ? "👑 you" : `👑 ${displayName || kingShort}`}
        </Text>
      </Billboard>
    </group>
  );
}

/**
 * A small gold crown that rides on the avatar capsule's head (~0.72 above the
 * capsule center): a banded ring with five points. Slowly spins for life.
 */
function Crown(): JSX.Element {
  const ref = useRef<THREE.Group>(null);
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.y += dt * 0.6;
  });
  const points = 5;
  return (
    <group ref={ref} position={[0, 0.72, 0]}>
      {/* band */}
      <mesh castShadow>
        <cylinderGeometry args={[0.17, 0.17, 0.1, 16, 1, true]} />
        <GoldMaterial />
      </mesh>
      {/* points around the band */}
      {Array.from({ length: points }).map((_, i) => {
        const a = (i / points) * Math.PI * 2;
        return (
          <mesh
            key={i}
            position={[Math.cos(a) * 0.17, 0.12, Math.sin(a) * 0.17]}
            castShadow
          >
            <coneGeometry args={[0.05, 0.14, 4]} />
            <GoldMaterial />
          </mesh>
        );
      })}
    </group>
  );
}

function GoldMaterial(): JSX.Element {
  return (
    <meshStandardMaterial
      color="#f4d36a"
      emissive="#c08020"
      emissiveIntensity={0.6}
      metalness={0.7}
      roughness={0.25}
    />
  );
}

function shortPk(pk: PublicKey): string {
  const s = pk.toBase58();
  return `${s.slice(0, 4)}…${s.slice(-4)}`;
}
